import { useMemo, useState, useEffect, ReactNode } from 'react'
import { Select, Divider, InputNumber, Button, Tooltip } from '@arco-design/web-react'
import useLocale from '@/utils/useLocale'
import styles from './index.module.less'

const Option = Select.Option

export type FilterItemType = 'select' | 'selectInput' | 'dateRangePicker' | 'multipleCheckbox' | 'multipleSelectInput'

interface FilterItemProps {
  title: ReactNode | string
  data: {
    label: string
    value: string
  }[]
  type: FilterItemType
  onChange?: (v: string | undefined) => void
  defaultValue?: string | undefined
  tooltipText?: string
}

// 筛选组件
const FilterItem = ({ title, data, type, onChange, defaultValue, tooltipText }: FilterItemProps) => {
  const t = useLocale()
  const [value, setValue] = useState<string | undefined>(undefined)
  const [minValue, setMinValue] = useState<number | undefined>()
  const [maxValue, setMaxValue] = useState<number | undefined>()
  const [popupVisible, setPopupVisible] = useState(false)

  useEffect(() => {
    // 兼容默认值：可能是选项值，或显示串（min-max / >min / <max），或内部自定义编码
    if (defaultValue === undefined || defaultValue === '') {
      setValue('')
      setMinValue(undefined)
      setMaxValue(undefined)
      return
    }
    if (typeof defaultValue === 'string') {
      const active = data.find((d) => d.value === defaultValue)
      if (active) {
        setValue(defaultValue)
        return
      }
      if (defaultValue.startsWith('__custom__|')) {
        const [, minStr = '', maxStr = ''] = defaultValue.split('|')
        const min = minStr !== '' ? Number(minStr) : undefined
        const max = maxStr !== '' ? Number(maxStr) : undefined
        setMinValue(min)
        setMaxValue(max)
        setValue(defaultValue)
        return
      }

      const rangeMatch = defaultValue.match(/^\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*$/)
      if (rangeMatch) {
        const min = Number(rangeMatch[1])
        const max = Number(rangeMatch[2])
        setMinValue(min)
        setMaxValue(max)
        setValue(`__custom__|${min}|${max}`)
        return
      }
      const gtMatch = defaultValue.match(/^>\s*(\d+(?:\.\d+)?)$/)
      if (gtMatch) {
        const min = Number(gtMatch[1])
        setMinValue(min)
        setMaxValue(undefined)
        setValue(`__custom__|${min}|`)
        return
      }
      const ltMatch = defaultValue.match(/^<\s*(\d+(?:\.\d+)?)$/)
      if (ltMatch) {
        const max = Number(ltMatch[1])
        setMinValue(undefined)
        setMaxValue(max)
        setValue(`__custom__||${max}`)
        return
      }
      // 默认认为是选项值
      setValue(defaultValue)
      setMinValue(undefined)
      setMaxValue(undefined)
    }
  }, [defaultValue])

  const triggerText = useMemo(() => {
    // 自定义区间显示：min-max / >min / <max
    if (typeof value === 'string' && value?.startsWith('__custom__')) {
      const [, min, max] = value.split('|') // __custom__|min|max
      const hasMin = !!min
      const hasMax = !!max
      const rangeText = hasMin && hasMax ? `${min}-${max}` : hasMin ? `>${min}` : hasMax ? `<${max}` : ''
      return rangeText ? `${title}: ${rangeText}` : title
    }
    // 选项显示
    const active = data.find((d) => d.value === value)
    if (active?.value === '') {
      return title
    }
    return active ? `${title}: ${active.label}` : title
  }, [data, title, value])

  const handleReset = () => {
    setValue('')
    onChange?.(undefined)
    setMinValue(undefined)
    setMaxValue(undefined)
    setPopupVisible(false)
  }

  const handleOk = () => {
    const custom = `__custom__|${minValue ?? ''}|${maxValue ?? ''}`
    setValue(custom)
    setPopupVisible(false)
    const display =
      minValue !== undefined && maxValue !== undefined
        ? `${minValue}-${maxValue}`
        : minValue !== undefined
        ? `>${minValue}`
        : maxValue !== undefined
        ? `<${maxValue}`
        : ''
    onChange?.(display || undefined)
  }

  return (
    <div className={styles.filterSelectWrap}>
      {tooltipText ? (
        <Tooltip content={tooltipText}>
          <Select
            className={styles.filterSelectView}
            value={value}
            placeholder={typeof title === 'string' ? title : ''}
            popupVisible={popupVisible}
            onClick={() => setPopupVisible(true)}
            onVisibleChange={(v) => setPopupVisible(v)}
            renderFormat={() => triggerText}
            triggerProps={{ autoAlignPopupWidth: false, autoAlignPopupMinWidth: true }}
            dropdownMenuStyle={{ maxHeight: 350 }}
            dropdownMenuClassName={styles.filterSelectPopup}
            onChange={(v) => {
              setValue(v as string)
              setMinValue(undefined)
              setMaxValue(undefined)
              setPopupVisible(false)
              onChange?.(v as string)
            }}
            dropdownRender={(menu) => {
              if (type === 'selectInput') {
                return (
                  <div>
                    {menu}
                    <Divider style={{ margin: 0 }} />
                    <div className={styles.customArea}>
                      <div className={styles.customInputs}>
                        <InputNumber
                          min={0}
                          className={styles.customInput}
                          hideControl
                          size="small"
                          value={minValue}
                          onChange={(val) => setMinValue(val)}
                        />
                        <span className={styles.hyphen}>-</span>
                        <InputNumber
                          min={0}
                          className={styles.customInput}
                          hideControl
                          size="small"
                          value={maxValue}
                          onChange={(val) => setMaxValue(val)}
                        />
                      </div>
                      <div className={styles.actions}>
                        <Button type="text" size="small" onClick={handleReset}>
                          {t['common.list.reset']}
                        </Button>
                        <Button className={styles.okBtn} type="primary" size="small" onClick={handleOk}>
                          {t['common.list.ok']}
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              }
              return menu
            }}
          >
            {data?.map((item) => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Tooltip>
      ) : (
        <Select
          className={styles.filterSelectView}
          value={value}
          placeholder={typeof title === 'string' ? title : ''}
          popupVisible={popupVisible}
          onClick={() => setPopupVisible(true)}
          onVisibleChange={(v) => setPopupVisible(v)}
          renderFormat={() => triggerText}
          triggerProps={{ autoAlignPopupWidth: false, autoAlignPopupMinWidth: true }}
          dropdownMenuStyle={{ maxHeight: 350 }}
          dropdownMenuClassName={styles.filterSelectPopup}
          onChange={(v) => {
            setValue(v as string)
            setMinValue(undefined)
            setMaxValue(undefined)
            setPopupVisible(false)
            onChange?.(v as string)
          }}
          dropdownRender={(menu) => {
            if (type === 'selectInput') {
              return (
                <div>
                  {menu}
                  <Divider style={{ margin: 0 }} />
                  <div className={styles.customArea}>
                    <div className={styles.customInputs}>
                      <InputNumber
                        min={0}
                        className={styles.customInput}
                        hideControl
                        size="small"
                        value={minValue}
                        onChange={(val) => setMinValue(val)}
                      />
                      <span className={styles.hyphen}>-</span>
                      <InputNumber
                        min={0}
                        className={styles.customInput}
                        hideControl
                        size="small"
                        value={maxValue}
                        onChange={(val) => setMaxValue(val)}
                      />
                    </div>
                    <div className={styles.actions}>
                      <Button type="text" size="small" onClick={handleReset}>
                        {t['common.list.reset']}
                      </Button>
                      <Button className={styles.okBtn} type="primary" size="small" onClick={handleOk}>
                        {t['common.list.ok']}
                      </Button>
                    </div>
                  </div>
                </div>
              )
            }
            return menu
          }}
        >
          {data?.map((item) => (
            <Option key={item.value} value={item.value}>
              {item.label}
            </Option>
          ))}
        </Select>
      )}
    </div>
  )
}

export default FilterItem
